import React, { useState } from 'react';
import './HandDrawnCheckbox.css';

interface HandDrawnCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  variant?: 'brand' | 'atacama' | 'iguazu' | 'mendoza' | 'patagonia' | 'santa-catarina';
  color?: 'awasi' | 'off-white' | 'white';
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
}

export const HandDrawnCheckbox: React.FC<HandDrawnCheckboxProps> = ({
  checked,
  onChange,
  label,
  variant = 'brand',
  color = 'awasi',
  className = '',
  ariaLabel,
  disabled = false,
  id
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) {
        onChange(!checked);
      }
    }
  };

  // Map variant names for Santa Catarina (file has space in name)
  const fileVariant = variant === 'santa-catarina' ? 'santa catarina' : variant;
  
  // Use the normal HandDrawnButton assets but make them square
  const defaultImagePath = `/Buttons/Normal/state=Default, color=${color}, animation=stroke to stroke, level=${fileVariant}.svg`;
  const hoverImagePath = `/Buttons/Normal/state=hover, color=${color}, animation=stroke to stroke, level=${fileVariant}.svg`;

  return (
    <label 
      className={`hand-drawn-checkbox hand-drawn-checkbox--${variant} hand-drawn-checkbox--${color} ${disabled ? 'hand-drawn-checkbox--disabled' : ''} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="hand-drawn-checkbox__input"
        id={id}
        aria-hidden="true"
        tabIndex={-1}
      />
      
      <div className={`hand-drawn-checkbox__visual ${checked ? 'hand-drawn-checkbox__visual--checked' : ''} ${isHovered ? 'hand-drawn-checkbox__visual--hover' : ''}`}>
        {/* Use Page Toggle Frame for checkbox border - only show frame when checked or hovered */}
        {(checked || isHovered) && (
          <svg 
            className="hand-drawn-checkbox__frame"
            preserveAspectRatio="xMidYMid meet"
            viewBox="0 0 37 39"
          >
            <image 
              href={`/Buttons/Page Toggle Frames/state=${checked ? 'selected' : 'hover'}, color=${color}.svg`}
              x="0" 
              y="0" 
              width="37" 
              height="39"
              preserveAspectRatio="xMidYMid meet"
            />
          </svg>
        )}
        {/* Default state - simple border */}
        {!checked && !isHovered && (
          <div className="hand-drawn-checkbox__default-border" />
        )}
        
        {checked && (
          <div className="hand-drawn-checkbox__checkmark">
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1.5 5L5 8.5L12.5 1"
                stroke={color === 'off-white' || color === 'white' ? '#49413d' : '#f3f3f3'}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="hand-drawn-checkbox__checkmark-path"
              />
            </svg>
          </div>
        )}
      </div>
      
      {label && (
        <span className="hand-drawn-checkbox__label">
          {label}
        </span>
      )}
    </label>
  );
};

export default HandDrawnCheckbox;